"use client";

import { c as _c } from "react/compiler-runtime";
import { AudioLines, Loader2 } from "lucide-react";
import { useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { getModelAudioConfig } from "@/lib/audio-gen";
import type { AudioFormat } from "@/lib/audio-gen";
interface AudioControlsProps {
  prompt: string;
  setPrompt: (prompt: string) => void;
  selectedModels: string[];
  voice: string;
  setVoice: (value: string) => void;
  audioFormat: AudioFormat;
  setAudioFormat: (value: AudioFormat) => void;
  speed: number;
  setSpeed: (value: number) => void;
  instructions: string;
  setInstructions: (value: string) => void;
  isGenerating: boolean;
  onGenerate: () => void;
}
export function AudioControls(t0) {
  const $ = _c(60);
  const {
    prompt,
    setPrompt,
    selectedModels,
    voice,
    setVoice,
    audioFormat,
    setAudioFormat,
    speed,
    setSpeed,
    instructions,
    setInstructions,
    isGenerating,
    onGenerate
  } = t0;
  const textareaRef = useRef(null);
  const primaryModel = selectedModels[0] ?? "";
  let canGenerate;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  if ($[0] !== audioFormat || $[1] !== instructions || $[2] !== isGenerating || $[3] !== onGenerate || $[4] !== primaryModel || $[5] !== prompt || $[6] !== selectedModels.length || $[7] !== setAudioFormat || $[8] !== setInstructions || $[9] !== setPrompt || $[10] !== setSpeed || $[11] !== setVoice || $[12] !== speed || $[13] !== voice) {
    const config = getModelAudioConfig(primaryModel);
    let t9;
    if ($[23] !== prompt || $[24] !== selectedModels.length) {
      t9 = prompt.trim().length > 0 && selectedModels.length > 0;
      $[23] = prompt;
      $[24] = selectedModels.length;
      $[25] = t9;
    } else {
      t9 = $[25];
    }
    canGenerate = t9;
    let t10;
    if ($[26] !== canGenerate || $[27] !== isGenerating || $[28] !== onGenerate || $[29] !== prompt) {
      t10 = e => {
        if (e.key === "Enter" && !e.shiftKey) {
          e.preventDefault();
          if (prompt.trim() && !isGenerating && canGenerate) {
            onGenerate();
          }
        }
      };
      $[26] = canGenerate;
      $[27] = isGenerating;
      $[28] = onGenerate;
      $[29] = prompt;
      $[30] = t10;
    } else {
      t10 = $[30];
    }
    const handleKeyDown = t10;
    t8 = "border-b bg-background p-4";
    t5 = "max-w-4xl mx-auto space-y-3";
    let t11;
    if ($[31] !== setPrompt) {
      t11 = e_0 => setPrompt(e_0.target.value);
      $[31] = setPrompt;
      $[32] = t11;
    } else {
      t11 = $[32];
    }
    if ($[33] !== handleKeyDown || $[34] !== isGenerating || $[35] !== prompt || $[36] !== t11) {
      t6 = <div className="rounded-md border-input border dark:bg-input/30 shadow-xs focus-within:ring-1 focus-within:ring-ring transition-colors"><Textarea ref={textareaRef} value={prompt} onChange={t11} onKeyDown={handleKeyDown} placeholder="Enter the text you want to turn into speech..." className="min-h-[80px] max-h-[200px] resize-none border-0 bg-transparent dark:bg-transparent focus-visible:ring-0 shadow-none" disabled={isGenerating} /></div>;
      $[33] = handleKeyDown;
      $[34] = isGenerating;
      $[35] = prompt;
      $[36] = t11;
      $[37] = t6;
    } else {
      t6 = $[37];
    }
    t7 = config.supportsInstructions && <Input value={instructions} onChange={e_1 => setInstructions(e_1.target.value)} placeholder="Optional style instructions, e.g. 'Speak in a warm, friendly tone'" disabled={isGenerating} />;
    t1 = "flex flex-wrap items-center gap-2";
    let t12;
    if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
      t12 = <SelectTrigger size="sm" className="min-w-[130px]"><SelectValue placeholder="Voice" /></SelectTrigger>;
      $[38] = t12;
    } else {
      t12 = $[38];
    }
    t2 = <Select value={voice} onValueChange={setVoice}>{t12}<SelectContent>{config.voices.map(_temp)}</SelectContent></Select>;
    t3 = config.availableFormats.length > 1 && <Select value={audioFormat} onValueChange={val => setAudioFormat(val as AudioFormat)}><SelectTrigger size="sm" className="min-w-[90px]"><SelectValue placeholder="Format" /></SelectTrigger><SelectContent>{config.availableFormats.map(_temp2)}</SelectContent></Select>;
    t4 = config.supportsSpeed && <Select value={String(speed)} onValueChange={val_0 => setSpeed(Number(val_0))}><SelectTrigger size="sm" className="min-w-[90px]"><SelectValue placeholder="Speed" /></SelectTrigger><SelectContent>{config.availableSpeeds.map(_temp3)}</SelectContent></Select>;
    $[0] = audioFormat;
    $[1] = instructions;
    $[2] = isGenerating;
    $[3] = onGenerate;
    $[4] = primaryModel;
    $[5] = prompt;
    $[6] = selectedModels.length;
    $[7] = setAudioFormat;
    $[8] = setInstructions;
    $[9] = setPrompt;
    $[10] = setSpeed;
    $[11] = setVoice;
    $[12] = speed;
    $[13] = voice;
    $[14] = canGenerate;
    $[15] = t1;
    $[16] = t2;
    $[17] = t3;
    $[18] = t4;
    $[19] = t5;
    $[20] = t6;
    $[21] = t7;
    $[22] = t8;
  } else {
    canGenerate = $[14];
    t1 = $[15];
    t2 = $[16];
    t3 = $[17];
    t4 = $[18];
    t5 = $[19];
    t6 = $[20];
    t7 = $[21];
    t8 = $[22];
  }
  let t9;
  if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <div className="flex-1" />;
    $[39] = t9;
  } else {
    t9 = $[39];
  }
  const t10 = isGenerating || !canGenerate;
  let t11;
  if ($[40] !== isGenerating) {
    t11 = isGenerating ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Generating...</> : <><AudioLines className="h-4 w-4 mr-2" />Generate</>;
    $[40] = isGenerating;
    $[41] = t11;
  } else {
    t11 = $[41];
  }
  let t12;
  if ($[42] !== onGenerate || $[43] !== t10 || $[44] !== t11) {
    t12 = <Button onClick={onGenerate} disabled={t10} className="min-w-[120px]">{t11}</Button>;
    $[42] = onGenerate;
    $[43] = t10;
    $[44] = t11;
    $[45] = t12;
  } else {
    t12 = $[45];
  }
  let t13;
  if ($[46] !== t1 || $[47] !== t12 || $[48] !== t2 || $[49] !== t3 || $[50] !== t4) {
    t13 = <div className={t1}>{t2}{t3}{t4}{t9}{t12}</div>;
    $[46] = t1;
    $[47] = t12;
    $[48] = t2;
    $[49] = t3;
    $[50] = t4;
    $[51] = t13;
  } else {
    t13 = $[51];
  }
  let t14;
  if ($[52] !== t13 || $[53] !== t5 || $[54] !== t6 || $[55] !== t7) {
    t14 = <div className={t5}>{t6}{t7}{t13}</div>;
    $[52] = t13;
    $[53] = t5;
    $[54] = t6;
    $[55] = t7;
    $[56] = t14;
  } else {
    t14 = $[56];
  }
  let t15;
  if ($[57] !== t14 || $[58] !== t8) {
    t15 = <div className={t8}>{t14}</div>;
    $[57] = t14;
    $[58] = t8;
    $[59] = t15;
  } else {
    t15 = $[59];
  }
  return t15;
}
function _temp3(s) {
  return <SelectItem key={s} value={String(s)}>{s}x</SelectItem>;
}
function _temp2(format) {
  return <SelectItem key={format} value={format}>{format.toUpperCase()}</SelectItem>;
}
function _temp(v) {
  return <SelectItem key={v} value={v}>{v.charAt(0).toUpperCase() + v.slice(1)}</SelectItem>;
}